import { useState } from 'react';
import { Modal, Button, Input } from '@realvisor/ui';
import { CloudArrowUpIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useProject } from '@/contexts/ProjectContext';
import { useCreateDocument } from '@/hooks';
import { api } from '@/lib/api';

interface UploadDocumentModalProps {
  open: boolean;
  onClose: () => void;
}

export function UploadDocumentModal({ open, onClose }: UploadDocumentModalProps) {
  const { projects, currentProject, isAllProjects } = useProject();
  const createDocument = useCreateDocument();
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [projectId, setProjectId] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Kontrola typu souboru
      if (selectedFile.type !== 'application/pdf') {
        toast.error('Pouze PDF soubory jsou podporovány');
        return;
      }
      
      // Kontrola velikosti (max 10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('Soubor je příliš velký (max 10MB)');
        return;
      }
      
      setFile(selectedFile);
      
      // Auto-fill title z názvu souboru
      if (!title) {
        const fileName = selectedFile.name.replace('.pdf', '');
        setTitle(fileName);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!file || !title.trim()) {
      toast.error('Vyplňte název a vyberte soubor');
      return;
    }

    setUploading(true);

    try {
      // 1. Upload souboru do Supabase Storage
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bucket', 'documents');
      formData.append('folder', projectId || 'general');

      const uploadResponse = await api.uploadFile<{ url: string }>('/upload', formData);

      // 2. Vytvoř záznam v databázi
      await createDocument.mutateAsync({
        title: title.trim(),
        description: description.trim() || undefined,
        projectId: projectId || undefined,
        fileUrl: uploadResponse.url,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
      });

      toast.success('Dokument nahrán');
      handleClose();
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error?.message || 'Nepodařilo se nahrát dokument');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setProjectId('');
    setFile(null);
    onClose();
  };

  return (
    <Modal isOpen={open} onClose={handleClose} title="Nahrát dokument">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Název dokumentu *
          </label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="např. Telefonní skripty"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Popis (volitelné)
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Krátký popis dokumentu..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            rows={3}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Projekt
          </label>
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">Obecný (pro všechny projekty)</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Obecné dokumenty jsou viditelné ve všech projektech
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Soubor (PDF) *
          </label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-primary-400 transition-colors">
            <div className="space-y-1 text-center">
              <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
              <div className="flex text-sm text-gray-600">
                <label className="relative cursor-pointer rounded-md font-medium text-primary-600 hover:text-primary-500">
                  <span>Vyberte soubor</span>
                  <input
                    type="file"
                    className="sr-only"
                    accept=".pdf,application/pdf"
                    onChange={handleFileChange}
                    required
                  />
                </label>
                <p className="pl-1">nebo přetáhněte</p>
              </div>
              <p className="text-xs text-gray-500">PDF do 10MB</p>
              {file && (
                <p className="text-sm font-medium text-primary-600 mt-2">
                  ✓ {file.name} ({(file.size / 1024).toFixed(0)} KB)
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={handleClose}>
            Zrušit
          </Button>
          <Button type="submit" disabled={uploading || !file || !title.trim()}>
            {uploading ? 'Nahrávám...' : 'Nahrát'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
